import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NAV_LINKS } from '../utils/constants';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMenuOpen && !target.closest('.mobile-menu-container') && !target.closest('.mobile-menu-button')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  // Reset active index when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setTimeout(() => {
        setActiveIndex(-1);
      }, 300);
    } else {
      // Stagger animation for menu items
      const timer = setTimeout(() => {
        setActiveIndex(0);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMenuOpen]);

  // Increment active index for staggered animations
  useEffect(() => {
    // Calculate the total number of menu items including conditional ones
    const totalItems = NAV_LINKS.length + (user ? 2 : 0) + (user?.isAdmin ? 1 : 0) - 1;
    
    if (activeIndex >= 0 && activeIndex < totalItems) {
      const timer = setTimeout(() => {
        setActiveIndex(activeIndex + 1);
      }, 50);
      return () => clearTimeout(timer);
    }
    // Only depend on activeIndex and user, since NAV_LINKS is a constant
  }, [activeIndex, user]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMenu();
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'text-snudefryd-pink' : 'text-gray-700 hover:text-snudefryd-blue';
  };

  const getMenuItemClass = (index: number) => {
    return activeIndex >= index 
      ? 'opacity-100 translate-x-0' 
      : 'opacity-0 translate-x-8';
  };

  return (
    <header className="bg-white shadow-sm relative z-50">
      <div className="container-custom py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo.png" 
              alt="Snudefryd Logo" 
              className="h-20 md:h-12 w-auto"
              onError={(e) => {
                // Fallback to text if image fails to load
                const imgElement = e.currentTarget;
                imgElement.style.display = 'none';
                
                // Find the text element and show it
                const parent = imgElement.parentElement;
                if (parent) {
                  const textElement = parent.querySelector('span');
                  if (textElement) {
                    textElement.style.display = 'block';
                  }
                }
              }} 
            />
            <span className="text-xl md:text-2xl font-bold text-snudefryd-blue" style={{display: 'none'}}>
              Snudefryd
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {NAV_LINKS.map((link) => (
              <Link 
                key={link.path} 
                to={link.path} 
                className={`font-medium ${isActive(link.path)}`}
              >
                {link.name}
              </Link>
            ))}
            
            {user?.isAdmin && (
              <Link to="/admin" className={`font-medium ${isActive('/admin')}`}>
                Admin
              </Link>
            )}
            {user && (
              <>
                <Link to="/profile" className={`font-medium ${isActive('/profile')}`}>
                  Min Profil
                </Link>
                <button
                  onClick={handleLogout}
                  className="font-medium text-gray-700 hover:text-snudefryd-pink"
                >
                  Log ud
                </button>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 focus:outline-none mobile-menu-button relative z-50"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <div className="w-8 h-8 flex flex-col items-center justify-center">
              <span 
                className={`block w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'}`}
              ></span>
              <span 
                className={`block w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ease-in-out ${isMenuOpen ? 'opacity-0 scale-0' : 'opacity-100'}`}
              ></span>
              <span 
                className={`block w-6 h-0.5 bg-gray-700 rounded-full transition-all duration-300 ease-in-out ${isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'}`}
              ></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation Overlay */}
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-500 md:hidden ${
            isMenuOpen ? 'opacity-60' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMenu}
        ></div>

        {/* Mobile Navigation Menu */}
        <div 
          className={`fixed right-0 top-0 h-full w-4/5 max-w-xs transform transition-transform duration-500 ease-in-out md:hidden mobile-menu-container ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '-5px 0 25px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-snudefryd-pink to-snudefryd-blue"></div>
          <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full opacity-10" 
               style={{ background: 'radial-gradient(circle, #ff6b6b 0%, transparent 70%)' }}></div>
          <div className="absolute bottom-20 left-0 w-16 h-16 rounded-tr-full opacity-10" 
               style={{ background: 'radial-gradient(circle, #4dabf7 0%, transparent 70%)' }}></div>
          
          <div className="p-6 h-full overflow-y-auto">
            <div className="flex justify-start items-center mb-10">
              <div className="text-xl font-bold text-gray-800">Menu</div>
            </div>
            
            <nav className="space-y-1">
              {NAV_LINKS.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center text-lg font-medium py-3 px-4 rounded-lg transition-all duration-300 ease-in-out transform ${getMenuItemClass(index)} ${
                    location.pathname === link.path 
                      ? 'bg-gradient-to-r from-snudefryd-pink/10 to-snudefryd-blue/10 text-snudefryd-pink' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={closeMenu}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <span className="ml-auto">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </Link>
              ))}
              
              {user?.isAdmin && (
                <Link
                  to="/admin"
                  className={`flex items-center text-lg font-medium py-3 px-4 rounded-lg transition-all duration-300 ease-in-out transform ${getMenuItemClass(NAV_LINKS.length)} ${
                    location.pathname === '/admin' 
                      ? 'bg-gradient-to-r from-snudefryd-pink/10 to-snudefryd-blue/10 text-snudefryd-pink' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={closeMenu}
                  style={{ transitionDelay: `${NAV_LINKS.length * 50}ms` }}
                >
                  Admin
                  {location.pathname === '/admin' && (
                    <span className="ml-auto">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </Link>
              )}
              
              {user && (
                <>
                  <Link
                    to="/profile"
                    className={`flex items-center text-lg font-medium py-3 px-4 rounded-lg transition-all duration-300 ease-in-out transform ${getMenuItemClass(NAV_LINKS.length + (user?.isAdmin ? 1 : 0))} ${
                      location.pathname === '/profile' 
                        ? 'bg-gradient-to-r from-snudefryd-pink/10 to-snudefryd-blue/10 text-snudefryd-pink' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={closeMenu}
                    style={{ transitionDelay: `${(NAV_LINKS.length + (user?.isAdmin ? 1 : 0)) * 50}ms` }}
                  >
                    Min Profil
                    {location.pathname === '/profile' && (
                      <span className="ml-auto">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className={`flex items-center w-full text-left text-lg font-medium py-3 px-4 rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-300 ease-in-out transform ${getMenuItemClass(NAV_LINKS.length + (user?.isAdmin ? 1 : 0) + 1)}`}
                    style={{ transitionDelay: `${(NAV_LINKS.length + (user?.isAdmin ? 1 : 0) + 1) * 50}ms` }}
                  >
                    <span>Log ud</span>
                    <span className="ml-auto">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </span>
                  </button>
                </>
              )}
            </nav>
            
            <div className="mt-auto pt-6 border-t border-gray-200 mt-10">
              <div className="opacity-0 animate-fadeIn" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
                <div className="flex items-center justify-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-snudefryd-pink to-snudefryd-blue flex items-center justify-center text-white font-bold text-lg">
                    S
                  </div>
                </div>
                <p className="text-sm text-center text-gray-500">© {new Date().getFullYear()} Snudefryd</p>
                <p className="text-xs text-center text-gray-400 mt-1">Alle rettigheder forbeholdes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 